import { modTypes } from "@blings/blings-player";
import { IModModel } from "../../stores/ModsStore";
import { IDynamicDataModel } from "../../stores/DynamicDataStore";
import { get } from "lodash";
import { IPlatformModel } from "../../stores/PlatformStore";
import {
  hasMediaFileExtension,
  isMediaContentType,
} from "../../helpers/mediaAssetValidation";
import {
  validateDynamicLocationValue,
  validateLocationMod,
} from "./locationConnector";
import { validateCalendarMod } from "./calendarConnector";

const alreadyTestedURLs = new Map<string, number>();

type DynamicValueResult =
  | {
      data: string;
      type: "err";
    }
  | {
      data: unknown;
      type: "dataKey" | "liveControlKey" | "value";
    }
  | {
      data: string;
      type: "expression";
    }
  | {
      data: "";
      type: "inputName";
    }
  | {
      data: null;
      type: null;
    };

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const getDynamicValueErrors = (dynamicValue: DynamicValueResult) => {
  const errors: Array<string> = [];
  if (dynamicValue.type === "err") {
    errors.push(dynamicValue.data);
  } else if (dynamicValue.data === null) errors.push("Value is not valid");
  return errors;
};

export const checkModDataConnectionErrors = async (
  mod: IModModel,
  dynamicDataStore: IDynamicDataModel,
  platformStore: IPlatformModel
) => {
  switch (mod.moddata.type) {
    case modTypes.text:
      return checkTextModErrors(mod, dynamicDataStore, platformStore);
    case modTypes.asset:
      return await checkAssetModErrors(mod, dynamicDataStore, platformStore);
    case modTypes.themeColor:
      return checkColorModErrors(mod, dynamicDataStore, platformStore);
    case modTypes.interactiveConcat:
      return checkInteractiveConcatModErrors(mod, platformStore);
    case modTypes.interactiveForm:
      return checkInteractiveFormModErrors(mod, platformStore);
    case modTypes.interactiveLocation:
      return checkInteractiveLocationModErrors(
        mod,
        dynamicDataStore,
        platformStore
      );
    case modTypes.interactiveCalendar:
      return checkInteractiveCalendarModErrors(mod);
  }
  return null;
};
const checkDynamicValueModErrors = (
  mod: IModModel,
  dynamicDataStore: IDynamicDataModel,
  platformStore: IPlatformModel
) => {
  return getDynamicValueErrors(
    getValueFromDynamicData(dynamicDataStore, platformStore, mod)
  );
};
const checkInteractiveLocationModErrors = (
  mod: IModModel,
  dynamicDataStore: IDynamicDataModel,
  platformStore: IPlatformModel
) => {
  const errors = validateLocationMod(mod.moddata as any);
  const hasDynamicSource = Boolean(
    mod.moddata.dataKey ||
      mod.moddata.liveControlKey ||
      mod.moddata.expression ||
      mod.moddata.inputName
  );

  if (hasDynamicSource) {
    errors.push(
      ...checkDynamicValueModErrors(mod, dynamicDataStore, platformStore)
    );
    const resolved = getValueFromDynamicData(
      dynamicDataStore,
      platformStore,
      mod
    );
    if (
      resolved.type !== "err" &&
      resolved.type !== "expression" &&
      resolved.type !== "inputName"
    ) {
      errors.push(
        ...validateDynamicLocationValue(
          resolved.data,
          mod.moddata.action as any
        )
      );
    }
  }

  const uniqueErrors = Array.from(new Set(errors));
  return uniqueErrors.length ? uniqueErrors : null;
};
const checkInteractiveCalendarModErrors = (mod: IModModel) => {
  // Calendar Code can reference viewer-time data and controls. Studio cannot
  // resolve it reliably; the Player validates the returned event at runtime.
  const errors = validateCalendarMod(mod.moddata as any);
  const uniqueErrors = Array.from(new Set(errors));
  return uniqueErrors.length ? uniqueErrors : null;
};
const checkTextModErrors = (
  mod: IModModel,
  dynamicDataStore: IDynamicDataModel,
  platformStore: IPlatformModel
) => {
  const errors = checkDynamicValueModErrors(
    mod,
    dynamicDataStore,
    platformStore
  );
  return errors.length ? errors : null;
};
const checkAssetModErrors = async (
  mod: IModModel,
  dynamicDataStore: IDynamicDataModel,
  platformStore: IPlatformModel
) => {
  const dynamicValue = getValueFromDynamicData(
    dynamicDataStore,
    platformStore,
    mod
  );
  const errors = getDynamicValueErrors(dynamicValue);
  const { value } = mod.moddata;
  if (dynamicValue.type === "err") return errors.length ? errors : null;
  if (dynamicValue.data) {
    let fileExist = 0;
    let assetUrl = dynamicValue.data;
    let expressionFailed = false;
    // If a return exists in the dynamic value, it means it is a function
    if (dynamicValue.type === "expression") {
      try {
        const result = await getEvalFunc(
          dynamicValue.data,
          JSON.stringify(dynamicDataStore.perVideoCurrentData),
          "''",
          JSON.stringify(dynamicDataStore.liveControlCurrentData)
        );
        assetUrl = result;
        // If the result exists in the already tested URLs, it means it has already been tested
        if (!isNonEmptyString(result)) fileExist = -255;
        else if (alreadyTestedURLs.has(result))
          fileExist = alreadyTestedURLs.get(result) ?? -255;
        else fileExist = await checkFileExists(result);
      } catch (e) {
        fileExist = -255;
        expressionFailed = true;
      }
    } else {
      if (!isNonEmptyString(dynamicValue.data)) fileExist = -255;
      else if (alreadyTestedURLs.has(dynamicValue.data))
        fileExist = alreadyTestedURLs.get(dynamicValue.data) ?? -255;
      else fileExist = await checkFileExists(dynamicValue.data);
    }
    switch (fileExist) {
      case 0:
        if (isNonEmptyString(assetUrl)) alreadyTestedURLs.set(assetUrl, 0);
        break;
      case -1:
        if (isNonEmptyString(assetUrl)) alreadyTestedURLs.set(assetUrl, -1);
        errors.push("Asset URL is not valid");
        break;
      case -255:
        if (isNonEmptyString(assetUrl)) alreadyTestedURLs.set(assetUrl, -255);
        errors.push(
          expressionFailed ? "Error in expression" : "Asset URL is not valid"
        );
        break;
    }
  } else if (value === "") errors.push("Asset URL is empty");
  return errors.length ? errors : null;
};

const checkColorModErrors = (
  mod: IModModel,
  dynamicDataStore: IDynamicDataModel,
  platformStore: IPlatformModel
) => {
  const errors = checkDynamicValueModErrors(
    mod,
    dynamicDataStore,
    platformStore
  );
  const { data } = getValueFromDynamicData(
    dynamicDataStore,
    platformStore,
    mod
  );
  if (errors.length) return errors;
  if (data) {
    const validColor = typeof data === "string" && isValidHexColorCode(data);
    if (!validColor) errors.push("Color code is not valid");
  }
  return errors.length ? errors : null;
};

const checkInteractiveConcatModErrors = (
  mod: IModModel,
  platformStore: IPlatformModel
) => {
  const errors: Array<string> = [];
  const { value, functionString } = mod.moddata;
  const scenes = new Set(
    platformStore.projectWorkspaceVersion?.videoParts?.map(
      (videoPart) => videoPart.name
    )
  );
  const flows = new Set(platformStore.lineups || []);

  if (functionString) {
    return null;
  }

  value?.forEach((item) => {
    if (typeof item === "string") {
      // Handle scene (string)
      if (!scenes.has(item)) {
        errors.push(`Scene ${item} does not exist`);
      }
    } else if (item && typeof item === "object" && "lineup" in item) {
      // Handle flow (object with lineup property)
      const flowItem = item as { lineup: string };
      if (!flows.has(flowItem.lineup)) {
        errors.push(`Flow ${flowItem.lineup} does not exist`);
      }
    } else {
      // Handle unexpected item types
      errors.push(`Invalid item type: ${JSON.stringify(item)}`);
    }
  });

  if (!value?.length) errors.push("No scenes or flows selected");
  return errors.length ? errors : null;
};
const checkInteractiveFormModErrors = (
  mod: IModModel,
  platformStore: IPlatformModel
) => {
  const errors: Array<string> = [];
  const { inputKeys } = mod.moddata;
  const { inputNames } = platformStore;
  if (!inputKeys) return ["No inputs selected"];
  inputKeys.forEach((inputName) => {
    if (!inputNames.includes(inputName)) {
      errors.push(`Input ${inputName} does not exist`);
    }
  });
  if (!inputKeys.length) errors.push("No inputs selected");
  return errors.length ? errors : null;
};
function checkFileExists(url: string) {
  return fetch(`https://cors.blings.io/${url.trim()}`, {
    method: "HEAD",
  })
    .then((response) => {
      if (!response.ok) {
        return -255; // File does not exist
      }

      const contentType = response.headers.get("content-type");
      if (isMediaContentType(contentType) || hasMediaFileExtension(url)) {
        return 0; // File exists
      }

      return -1; // File exists but is not a supported media file
    })
    .catch(() => {
      return -255; // Error occurred, file does not exist
    });
}

function isValidHexColorCode(colorCode: string) {
  const hexColorRegex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
  return hexColorRegex.test(colorCode);
}

function getValueFromDynamicData(
  dynamicDataStore: IDynamicDataModel,
  platformStore: IPlatformModel,
  mod: IModModel
): DynamicValueResult {
  const { moddata } = mod;
  if (moddata.dataKey) {
    const { dataKey } = moddata;
    const currentData = dynamicDataStore.perVideoCurrentData;
    const data = get(currentData, dataKey);
    if (data === undefined) {
      return {
        data: "Invalid data key",
        type: "err",
      };
    }
    return {
      data,
      type: "dataKey",
    };
  } else if (moddata.liveControlKey) {
    const { liveControlKey } = moddata;
    const currentData = dynamicDataStore.liveControlCurrentData;
    const data = get(currentData, liveControlKey);
    if (data === undefined) {
      return {
        data: "Invalid live control key",
        type: "err",
      };
    }
    return {
      data,
      type: "liveControlKey",
    };
  } else if (moddata.value != null) {
    const { value } = moddata;
    return {
      data: value,
      type: "value",
    };
  } else if (moddata.expression != null) {
    const { expression } = moddata;
    return {
      data: expression,
      type: "expression",
    };
  } else if (moddata.inputName != null) {
    const { inputName } = moddata;
    const { inputNames } = platformStore;
    if (!inputNames.includes(inputName)) {
      return {
        data: "Invalid input name",
        type: "err",
      };
    }
    return {
      data: "",
      type: "inputName",
    };
  }
  return {
    data: null,
    type: null,
  };
}

function getEvalFunc(
  expression: string,
  data: string,
  inputs: string,
  control: string
) {
  // todo: DB stored value should be the full expression, not only the internals of that, similar to interactive connector.
  // make sure to have backward compatibility

  const toEvalStrFunc = `
  var d = ${data};
  var inp = ${inputs};
  var c = ${control};
  
  (async function x(data, inputs, control) {
    ${expression}
  })(d,inp,c);
`;
  // eslint-disable-next-line no-eval -- Expression mod requires evaluating user-provided code.
  return eval(toEvalStrFunc);
}
